/**
 * Pill buttons — the prototype `.btn` recipe (999px radius, four variants).
 * Server-safe. `PillButton` renders a `<button>`; `PillLinkButton` renders a
 * next/link `<a>` (pass `plainAnchor` for editor links needing full reloads).
 */
import Link from "next/link";
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { cx } from "@/components/ui/cx";

export type PillVariant = "primary" | "secondary" | "ghost" | "danger";
export type PillSize = "sm" | "md";

export interface PillStyleProps {
  variant?: PillVariant;
  size?: PillSize;
  /** Full-width (`width:100%`). */
  block?: boolean;
}

const VARIANT_CLASS: Record<PillVariant, string> = {
  primary: "tdo-btn-primary",
  secondary: "tdo-btn-secondary",
  ghost: "tdo-btn-ghost",
  danger: "tdo-btn-danger",
};

/** Class helper for non-component cases (e.g. styling a form submit). */
export function pillButtonClasses({
  variant = "primary",
  size = "md",
  block = false,
  className,
}: PillStyleProps & { className?: string } = {}): string {
  return cx(
    "tdo-btn",
    VARIANT_CLASS[variant],
    size === "sm" && "tdo-btn-sm",
    block && "tdo-btn-block",
    className,
  );
}

export interface PillButtonProps
  extends PillStyleProps,
    ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
}

export function PillButton({
  variant = "primary",
  size = "md",
  block = false,
  className,
  type = "button",
  children,
  ...rest
}: PillButtonProps) {
  return (
    <button
      type={type}
      className={pillButtonClasses({ variant, size, block, className })}
      {...rest}
    >
      {children}
    </button>
  );
}

export interface PillLinkButtonProps
  extends PillStyleProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href: string;
  /** Render a raw `<a>` (full document load) instead of next/link. */
  plainAnchor?: boolean;
  children: ReactNode;
}

export function PillLinkButton({
  href,
  plainAnchor = false,
  variant = "primary",
  size = "md",
  block = false,
  className,
  children,
  ...rest
}: PillLinkButtonProps) {
  const cls = pillButtonClasses({ variant, size, block, className });
  if (plainAnchor) {
    return (
      <a href={href} className={cls} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={cls} {...rest}>
      {children}
    </Link>
  );
}
